import { useState, useEffect, useMemo } from 'react';
import { Box, Button } from '@mui/material';
import { useNavigate } from 'react-router';
import { CaretLeft } from '@phosphor-icons/react';
import WizardStepper from './WizardStepper';
import WizardNavigation from './WizardNavigation';
import AgentTypeSelection from './steps/AgentTypeSelection';
import IntegrationSelection from './steps/IntegrationSelection';
import AgentDefinition from './steps/AgentDefinition';
import AgentPersonalization from './steps/AgentPersonalization';
import AgentAnalytics from './steps/AgentAnalytics';
import CompletionStep from './steps/CompletionStep';

export interface AgentData {
  agentType: 'voice' | 'whatsapp' | null;
  integrationType: 'crm' | 'hms' | 'pos' | null;
  integrationConfig: any;
  definition: any;
  personalization: any;
  analytics: any;
}

const STORAGE_KEY = 'onboarding_agent_data';
const STORAGE_STEP_KEY = 'onboarding_agent_step';
const STORAGE_COMPLETED_KEY = 'onboarding_agent_completed';

const steps = [
  { label: 'Agent Type', emoji: '🤖' },
  { label: 'Integration', emoji: '🔌' },
  { label: 'Definition', emoji: '📝' },
  { label: 'Personalization', emoji: '✨' },
  { label: 'Analytics', emoji: '📊' },
  { label: 'Complete', emoji: '🎉' },
];

const AgentCreationWizard = () => {
  const navigate = useNavigate();

  // Check if business setup is complete
  const businessData = useMemo(() => {
    const saved = localStorage.getItem('onboarding_business_data');
    return saved ? JSON.parse(saved) : null;
  }, []);

  // Redirect to business setup if not complete
  useEffect(() => {
    if (
      !businessData ||
      !businessData.brands?.length ||
      !businessData.stores?.length
    ) {
      navigate('/onboarding');
    }
  }, [businessData, navigate]);

  // Initialize state from localStorage if available
  const [currentStep, setCurrentStep] = useState<number>(() => {
    const saved = localStorage.getItem(STORAGE_STEP_KEY);
    return saved ? parseInt(saved, 10) : 0;
  });

  const [completedSteps, setCompletedSteps] = useState<Set<number>>(() => {
    const saved = localStorage.getItem(STORAGE_COMPLETED_KEY);
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });

  const [agentData, setAgentData] = useState<AgentData>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved
      ? JSON.parse(saved)
      : {
          agentType: null,
          integrationType: null,
          integrationConfig: {},
          definition: {},
          personalization: {},
          analytics: {},
        };
  });

  const [isStepValid, setIsStepValid] = useState(false);

  // Persist to localStorage whenever data changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(agentData));
  }, [agentData]);

  useEffect(() => {
    localStorage.setItem(STORAGE_STEP_KEY, currentStep.toString());
  }, [currentStep]);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_COMPLETED_KEY,
      JSON.stringify([...completedSteps])
    );
  }, [completedSteps]);

  const handleNext = () => {
    // Mark current step as completed
    setCompletedSteps(prev => new Set([...prev, currentStep]));

    // Move to next step
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    // Clear all onboarding data from localStorage
    localStorage.removeItem('onboarding_business_data');
    localStorage.removeItem('onboarding_business_step');
    localStorage.removeItem('onboarding_business_completed');
    localStorage.removeItem('onboarding_business_complete');
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_STEP_KEY);
    localStorage.removeItem(STORAGE_COMPLETED_KEY);

    // Navigate to dashboard
    navigate('/');
  };

  const updateAgentData = (updates: Partial<AgentData>) => {
    setAgentData(prev => ({
      ...prev,
      ...updates,
    }));
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <AgentTypeSelection
            selectedType={agentData.agentType}
            onTypeChange={agentType => updateAgentData({ agentType })}
            onValidationChange={setIsStepValid}
          />
        );
      case 1:
        return (
          <IntegrationSelection
            selectedType={agentData.integrationType}
            config={agentData.integrationConfig}
            onIntegrationChange={(integrationType, integrationConfig) =>
              updateAgentData({ integrationType, integrationConfig })
            }
            onValidationChange={setIsStepValid}
            stores={businessData?.stores ?? []}
          />
        );
      case 2:
        return (
          <AgentDefinition
            agentType={agentData.agentType}
            definition={agentData.definition}
            onDefinitionChange={definition => updateAgentData({ definition })}
            onValidationChange={setIsStepValid}
          />
        );
      case 3:
        return (
          <AgentPersonalization
            personalization={agentData.personalization}
            onPersonalizationChange={personalization =>
              updateAgentData({ personalization })
            }
            onValidationChange={setIsStepValid}
          />
        );
      case 4:
        return (
          <AgentAnalytics
            analytics={agentData.analytics}
            onAnalyticsChange={analytics => updateAgentData({ analytics })}
            onValidationChange={setIsStepValid}
          />
        );
      case 5:
        return (
          <CompletionStep
            wizardData={{ ...businessData, agent: agentData }}
            onComplete={handleComplete}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        bgcolor: 'secondary.main',
      }}
    >
      {/* Top: Stepper */}
      <WizardStepper
        currentStep={currentStep}
        completedSteps={completedSteps}
        steps={steps}
      />

      {/* Middle: Step Content with Back to Business Setup button */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Back to Business Setup button - only on first step */}
        {currentStep === 0 && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="text"
              startIcon={<CaretLeft size={16} weight="bold" />}
              onClick={() => navigate('/onboarding')}
              sx={{
                color: 'lightGray.text',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                '&:hover': {
                  color: 'custom.textBlue',
                  bgcolor: 'custom.sidebarActiveBg',
                },
              }}
            >
              Back to Business Setup
            </Button>
          </Box>
        )}

        {renderCurrentStep()}
      </Box>

      {/* Bottom: Navigation (hide on completion step) */}
      {currentStep !== 5 && (
        <WizardNavigation
          currentStep={currentStep}
          onBack={handleBack}
          onNext={handleNext}
          canProceed={isStepValid}
          showBack={currentStep > 0}
        />
      )}
    </Box>
  );
};

export default AgentCreationWizard;
